import React from "react";

// Styles
import * as s from "./App.styles";

import { createTheme, ThemeProvider } from '@mui/material/styles';
import {ThemeProvider as SCThemeProvider} from 'styled-components';

// Icons
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import FolderSpecialRoundedIcon from '@mui/icons-material/FolderSpecialRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

// Routing
import { Provider } from "react-redux";
import {BrowserRouter, Route, Routes} from "react-router-dom";

// Components
import Sidebar from "./components/Sidebar/Sidebar";
import MainView from "./components/MainView/MainView";
import Store from "./components/Store";
import RenderOnAnonymous from "./components/RenderOnAnonymous";
import RenderOnAuthenticated from "./components/RenderOnAuthenticated";

// Services
import StoreService from "./services/StoreService";
import Welcome from "./app/Welcome";
import Redirect from "./app/Redirect";

import {KEYCLOAK_ALLOWED_REALMS} from './modules/config';

// Var
import { MENU1, MENU2, MENU3, MENU4, MENU5, MENU9, MENU10 } from "./modules/config";
import RealmsRoute from "./components/RealmsRoute";


let realmId = "";
if (sessionStorage.getItem("realm_stuff")) {
    realmId = sessionStorage.getItem("realm_stuff");
}

const store = StoreService.setup();


const theme = createTheme({
    palette: {
        primary: {
            main: '#1565C0',
            light: '#42A5F5',
            dark: '#0D47A1',
        },
        secondary: {
            main: '#42A5F5',
        },
        background: {
            default: '#F0F4F8',
            paper: '#ffffff',
        },
    },
    shape: {
        borderRadius: 8,
    },
    typography: {
        fontFamily: "poppins",
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    components: {
        MuiAppBar: {
            defaultProps: { position: 'static' },
            styleOverrides: {
                root: {
                    background: 'linear-gradient(90deg, #0D1B2A 0%, #1B3A5C 100%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                }
            }
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    minHeight: '56px',
                    "&.MuiTablePagination-toolbar": { width: 'auto', minHeight: '48px' }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    minHeight: '44px',
                    padding: '10px 20px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                },
                contained: {
                    background: 'linear-gradient(135deg, #0D1B2A 0%, #1565C0 100%)',
                    color: '#ffffff',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #1B3A5C 0%, #1976D2 100%)',
                    },
                },
                outlined: {
                    borderColor: '#1565C0',
                    color: '#1565C0',
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderColor: '#0D47A1',
                        borderWidth: '1.5px',
                        backgroundColor: 'rgba(21,101,192,0.06)',
                    },
                },
                text: {
                    color: '#1565C0',
                    '&:hover': { backgroundColor: 'rgba(21,101,192,0.06)' },
                },
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1565C0',
                        borderWidth: '1.5px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#42A5F5',
                        borderWidth: '2px',
                    },
                },
                input: {
                    padding: '12px 14px',
                    minHeight: '20px',
                },
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': { color: '#1565C0' },
                },
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                },
                elevation1: {
                    boxShadow: '0 2px 12px rgba(13,27,42,0.08)',
                },
                elevation2: {
                    boxShadow: '0 4px 16px rgba(13,27,42,0.1)',
                },
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: '12px',
                    boxShadow: '0 8px 40px rgba(13,27,42,0.2)',
                },
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: '48px',
                    fontWeight: 500,
                    '&.Mui-selected': { color: '#1565C0', fontWeight: 700 },
                },
            }
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#42A5F5',
                    height: '3px',
                    borderRadius: '2px',
                },
            }
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 600,
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                },
                body: {
                    padding: '14px 16px',
                    fontSize: '14px',
                },
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: 'rgba(21,101,192,0.04) !important',
                    },
                },
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: '6px',
                    fontWeight: 500,
                },
                colorPrimary: {
                    backgroundColor: 'rgba(21,101,192,0.1)',
                    color: '#1565C0',
                },
            }
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    padding: '12px 14px',
                },
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(13,27,42,0.15)',
                },
                option: {
                    padding: '10px 14px',
                    '&:hover': { backgroundColor: 'rgba(21,101,192,0.06)' },
                    '&[aria-selected="true"]': {
                        backgroundColor: 'rgba(21,101,192,0.12) !important',
                        color: '#1565C0',
                        fontWeight: 600,
                    },
                },
            }
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    borderRadius: '6px',
                },
            }
        },
    }
});

const App = () => {
    const sidebarHeader = {
        fullName: "ImmoSpector twin",
        shortName: "twin",
    };

    // Menü-Items im linken Menü
    const menuItems = [
        {
            name: MENU1,
            to: "/",
            icon: <HomeRoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'MANDANT_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_READER', 'TWIN_DATA_ADMIN'],
        },
        {
            name: MENU2,
            to: "/TreeComponent",
            icon: <AccountTreeRoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_READER', 'TWIN_DATA_ADMIN'],
        },
        {
            name: MENU3,
            to: "/BimProfile",
            icon: <BadgeRoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_READER', 'TWIN_DATA_ADMIN'],
        },
        {
            name: MENU4,
            to: "/BimProjects",
            icon: <FolderSpecialRoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_READER', 'TWIN_DATA_ADMIN'],
        },
        {
            name: 'Objekte',
            to: `/DataobjectsList/${realmId}`,
            icon: <Inventory2RoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_READER', 'TWIN_DATA_ADMIN']
        },
        {
            name: 'Bauteile',
            to: `/DataComponents/${realmId}`,
            icon: <ConstructionRoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_READER', 'TWIN_DATA_ADMIN']
        },
        {
            name: 'Import',
            to: `/Import`,
            icon: <CloudUploadRoundedIcon />,
            subMenuItems: [
                {
                    name: "CSV/Excel-Dateien",
                    to: `/uploadfiles/${realmId}`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_ADMIN'],
                },
                {
                    name: "WU-DB",
                    to: "/wuimport",
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_DATA_ADMIN'],
                },
                {
                    name: MENU9,
                    to: "/BimetaData",
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_ADMIN'],
                }
            ],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_ADMIN']
        },
        {
            name: MENU10,
            to: `/ObjectFields/${realmId}`,
            icon: <TuneRoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_ADMIN'],
        },
        {
            name: "Benutzer",
            to: `/MandantUserList/${realmId}`,
            icon: <GroupRoundedIcon />,
            subMenuItems: [],
            rollen: ["USER_ADMIN_MANDANT", 'ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN'],
        },
        {
            name: "API-Doku",
            to: ``,
            icon: <CodeRoundedIcon />,
            subMenuItems: [
                {
                    name: "Bauteiltypen",
                    to: `/SwaggerDoku/${realmId}/openapi_componenttypes.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Bauteile OGC",
                    to: `/SwaggerDoku/${realmId}/openapi_component.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Attachments",
                    to: `/SwaggerDoku/${realmId}/openapi_attachments.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Feature OGC",
                    to: `/SwaggerDoku/${realmId}/openapi3_objects.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Immobilienobjekte",
                    to: `/SwaggerDoku/${realmId}/twinapi_realestateobject.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Verwaltungsobjekte",
                    to: `/SwaggerDoku/${realmId}/twinapi_administrativeobject.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Bauteile intern",
                    to: `/SwaggerDoku/${realmId}/twinapi_component.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Profile",
                    to: `/SwaggerDoku/${realmId}/twinapi_bimprofile.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Projekte",
                    to: `/SwaggerDoku/${realmId}/twinapi_projecti.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                },
                {
                    name: "Upload",
                    to: `/SwaggerDoku/${realmId}/twinapi_fileupload.json`,
                    subMenuItems: [],
                    rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
                }
            ],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_API'],
        },
        {
            name: MENU5,
            to: "/logout",
            icon: <LogoutRoundedIcon />,
            subMenuItems: [],
            rollen: ['ADMIN_MAP', 'MAP_ADMIN', 'TWIN_MANDANT_ADMIN', 'TWIN_DATA_WRITER', 'TWIN_DATA_READER', 'TWIN_DATA_ADMIN'],
        },
    ];

    const fonts = {
        header: "Poppins",
        menu: "Poppins",
    };

    return (
        <Provider store={store}>
            <BrowserRouter>
                <Store>
                    <ThemeProvider theme={theme}>
                        <SCThemeProvider theme={theme}>
                        <s.App>
                            <RenderOnAnonymous realmId={realmId}>
                                <Routes>
                                    <Route path="/:realm" element={<RealmsRoute realms={KEYCLOAK_ALLOWED_REALMS}><Redirect /></RealmsRoute>}></Route>
                                    <Route path="*" element={<Welcome />}></Route>
                                </Routes>

                            </RenderOnAnonymous>
                            <RenderOnAuthenticated realmId={realmId}>
                                <Sidebar
                                    sidebarHeader={sidebarHeader}
                                    menuItems={menuItems}
                                    fonts={fonts}
                                    colorPalette={s.navySteel}
                                />
                                <MainView theme={theme}/>
                            </RenderOnAuthenticated>
                        </s.App>
                        </SCThemeProvider>
                    </ThemeProvider>
                </Store>
            </BrowserRouter>
        </Provider>
    );
};

export default App;
